import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, ArrowRight, Package, Clock, Sparkles, CheckCircle, Phone, Mail, Users, Shield, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { SEOHead } from "@/components/SEOHead";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfessionnelsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nom: "", prenom: "", email: "", telephone: "", adresse: "",
    type_activite: "", surface: "", frequence: "", horaire_souhaite: "",
    fournir_produits: false, fournir_consommables: false, message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demande-pro`, {
        ...form,
        surface: parseFloat(form.surface)
      });
      toast.success("Demande envoyée avec succès !", {
        description: "Nous vous recontacterons sous 48h."
      });
      navigate("/");
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const frequences = [
    { value: "ponctuel", label: "Ponctuel" },
    { value: "quotidien", label: "Quotidien" },
    { value: "hebdomadaire", label: "Hebdomadaire" },
    { value: "bi_mensuel", label: "2 fois / mois" },
    { value: "mensuel", label: "Mensuel" },
  ];

  const typesActivite = [
    "Bureau", "Commerce", "Restaurant", "Cabinet médical", 
    "Salle de sport", "Copropriété", "Entrepôt", "Autre"
  ];

  const features = [
    { icon: Users, title: "Équipe dédiée", desc: "Personnel formé pour vos locaux" },
    { icon: Shield, title: "Assurance RC Pro", desc: "Couverture complète" },
    { icon: Award, title: "Qualité garantie", desc: "Satisfaction ou reprise" },
    { icon: Clock, title: "Flexibilité", desc: "Horaires adaptés à vos besoins" },
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50" data-testid="pro-page">
      <SEOHead 
        title="Nettoyage Professionnel pour Entreprises — Hérault"
        description="IKS Propreté & Services : entreprise de nettoyage pour professionnels dans l'Hérault. Entretien de bureaux, commerces, copropriétés. Devis gratuit et personnalisé. Montpellier, Béziers, Sète et tout le 34."
        path="/professionnels"
        keywords="entreprise de nettoyage professionnel Hérault, nettoyage bureaux Montpellier, entretien locaux professionnels 34, nettoyage copropriété Hérault, société nettoyage entreprise Montpellier"
      />
      
      {/* Hero */}
      <section className="bg-[#003264] py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white/80 text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Offre Professionnels
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Entreprise de nettoyage professionnel dans l'Hérault
            </h1>
            <p className="text-xl text-white/70 mb-8">
              Bureaux, commerces, copropriétés à Montpellier et dans tout le 34 — devis personnalisé gratuit, qualité garantie.
            </p>
            
            {/* Pricing */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-md">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                <p className="text-white/60 text-sm mb-1">À partir de</p>
                <p className="text-4xl font-bold text-white">2,50€</p>
                <p className="text-white/60 text-sm">/ m² TTC</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                <p className="text-white/60 text-sm mb-1">À partir de</p>
                <p className="text-4xl font-bold text-white">40€</p>
                <p className="text-white/60 text-sm">/ heure TTC</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#003264]/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-[#003264]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{feature.title}</p>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-[#003264] mb-3">Demande de devis gratuit</h2>
            <p className="text-gray-600">Remplissez ce formulaire, nous vous recontactons sous 48h</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="form-pro">
            
            {/* Section 1: Contact */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[#003264] flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#003264] text-white text-sm flex items-center justify-center">1</span>
                  Vos coordonnées
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-gray-700">Nom *</Label>
                    <Input name="nom" value={form.nom} onChange={handleChange} required className="mt-1.5 h-12" data-testid="input-nom" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Prénom *</Label>
                    <Input name="prenom" value={form.prenom} onChange={handleChange} required className="mt-1.5 h-12" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Email *</Label>
                    <Input name="email" type="email" value={form.email} onChange={handleChange} required className="mt-1.5 h-12" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Téléphone *</Label>
                    <Input name="telephone" value={form.telephone} onChange={handleChange} required className="mt-1.5 h-12" />
                  </div>
                </div>
                <div className="mt-5">
                  <Label className="text-gray-700">Adresse des locaux *</Label>
                  <Input name="adresse" value={form.adresse} onChange={handleChange} required className="mt-1.5 h-12" placeholder="Adresse complète" />
                </div>
              </div>
            </div>

            {/* Section 2: Locaux */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[#003264] flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#003264] text-white text-sm flex items-center justify-center">2</span>
                  Vos locaux
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-gray-700">Type d'activité *</Label>
                    <Select value={form.type_activite} onValueChange={(v) => setForm(prev => ({ ...prev, type_activite: v }))}>
                      <SelectTrigger className="mt-1.5 h-12">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {typesActivite.map(t => (
                          <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Surface (m²) *</Label>
                    <Input name="surface" type="number" value={form.surface} onChange={handleChange} required className="mt-1.5 h-12" placeholder="Ex: 200" />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Planning */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[#003264] flex items-center gap-3">
                  <Clock className="w-5 h-5" />
                  Planning souhaité
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-gray-700">Fréquence *</Label>
                    <Select value={form.frequence} onValueChange={(v) => setForm(prev => ({ ...prev, frequence: v }))}>
                      <SelectTrigger className="mt-1.5 h-12">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequences.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Horaires souhaités *</Label>
                    <Input 
                      name="horaire_souhaite" 
                      value={form.horaire_souhaite} 
                      onChange={handleChange} 
                      required 
                      className="mt-1.5 h-12" 
                      placeholder="Ex: 6h-8h ou soir après 18h"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Fournitures */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[#003264] flex items-center gap-3">
                  <Package className="w-5 h-5" />
                  Fournitures
                </h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">Devons-nous fournir le matériel ?</p>
                <div className="space-y-3">
                  <label className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#003264]/30 cursor-pointer transition-colors">
                    <Checkbox 
                      id="produits"
                      checked={form.fournir_produits}
                      onCheckedChange={(c) => setForm(prev => ({ ...prev, fournir_produits: c }))}
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Produits de nettoyage</p>
                      <p className="text-sm text-gray-500">Détergents, désinfectants, etc.</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#003264]/30 cursor-pointer transition-colors">
                    <Checkbox 
                      id="consommables"
                      checked={form.fournir_consommables}
                      onCheckedChange={(c) => setForm(prev => ({ ...prev, fournir_consommables: c }))}
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Consommables</p>
                      <p className="text-sm text-gray-500">Papier toilette, essuie-mains, savon, sacs poubelle...</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Section 5: Message */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[#003264] flex items-center gap-3">
                  <Sparkles className="w-5 h-5" />
                  Besoins spécifiques
                </h3>
              </div>
              <div className="p-6">
                <Textarea 
                  name="message" 
                  value={form.message} 
                  onChange={handleChange} 
                  rows={4} 
                  className="resize-none"
                  placeholder="Décrivez vos besoins particuliers : type de sols, équipements spéciaux, contraintes horaires..."
                />
              </div>
            </div>

            {/* Submit */}
            <div className="text-center">
              <Button 
                type="submit" 
                disabled={loading} 
                className="bg-[#003264] hover:bg-[#002244] text-white px-12 py-6 text-base btn-shine"
              >
                {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-gray-500 mt-4">
                <CheckCircle className="w-4 h-4 inline mr-1 text-emerald-500" />
                Réponse sous 48h • Devis gratuit et sans engagement
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white border-t">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-xl font-bold text-[#003264] mb-4">Besoin d'une réponse rapide ?</h3>
          <p className="text-gray-600 mb-6">Contactez-nous directement</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:0625168027">
              <Button variant="outline" className="border-[#003264] text-[#003264] hover:bg-[#003264] hover:text-white px-8">
                <Phone className="w-4 h-4 mr-2" />
                06 25 16 80 27
              </Button>
            </a>
            <a href="mailto:contact@iks-services.fr">
              <Button variant="outline" className="border-[#003264] text-[#003264] hover:bg-[#003264] hover:text-white px-8">
                <Mail className="w-4 h-4 mr-2" />
                contact@iks-services.fr
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
